'use client';

import React from 'react';

import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';

import { useUserInfo } from '@/context/UserInfoContext';
import { FEEDBACK_TRIGGERS, useFeedback, type Target } from '@/feedback';

/** Props for the shared button used to file targeted feedback. */
type FileFeedbackButtonProps = Omit<IconButtonProps, 'children' | 'onClick'> & {
  /** Identifies the content associated with the feedback request. */
  target: Target;
};

/**
 * Shared IconButton for filing targeted feedback that is safe inside clickable cards.
 *
 * Self-gates on auth: renders nothing for signed-out users, since filing feedback
 * requires an authenticated session. This mirrors the OmniBoxMenu self-gate convention,
 * so call sites can render it without wrapping it in their own `userInfo` guard.
 */
export const FileFeedbackButton: React.FC<FileFeedbackButtonProps> = ({
  target,
  size = 'small',
  title = 'Give feedback',
  'aria-label': ariaLabel = 'Give feedback',
  ...props
}) => {
  const { openFeedback } = useFeedback();
  const { userInfo } = useUserInfo();

  if (!userInfo) {
    return null;
  }

  return (
    <IconButton
      {...props}
      type="button"
      size={size}
      title={title}
      aria-label={ariaLabel}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openFeedback({ trigger: FEEDBACK_TRIGGERS.FLAG, target });
      }}
    >
      <FeedbackOutlinedIcon fontSize="inherit" />
    </IconButton>
  );
};
